import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getLookups } from '../services/lookupsApi';

const LookupsContext = createContext(null);

/**
 * Provides application-wide lookup data (e.g., units, difficulty levels).
 * Fetches the data once on initial load and makes it available to all child components.
 * 
 * Updated to distinguish between Activity measurements (sets, reps) and Profile measurements (height, weight).
 */
export const LookupsProvider = ({ children }) => {
  const [lookups, setLookups] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const lookupsData = await getLookups();
        setLookups(lookupsData);
      } catch (err) {
        setError(err);
        console.error("Failed to load application lookups:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLookups();
  }, []);

  const value = useMemo(() => ({
    lookups,
    // Explicitly expose categorized measurement types for clarity
    // measurement_types: Activity-related (WEIGHT, REPS, DISTANCE)
    activityMeasurementTypes: lookups?.measurement_types || {},
    // profile_measurement_types: Profile-related (PROFILE_HEIGHT, PROFILE_WEIGHT)
    profileMeasurementTypes: lookups?.profile_measurement_types || {},
    isLoading,
    error,
  }), [lookups, isLoading, error]);

  return (
    <LookupsContext.Provider value={value}>
      {children}
    </LookupsContext.Provider>
  );
};

/**
 * Custom hook to access the lookups context.
 * @returns {{
 *   lookups: object | null, 
 *   activityMeasurementTypes: object,
 *   profileMeasurementTypes: object,
 *   isLoading: boolean, 
 *   error: Error | null
 * }}
 */
export const useLookups = () => {
  const context = useContext(LookupsContext);
  if (context === null) {
    throw new Error('useLookups must be used within a LookupsProvider');
  }
  return context;
};